import React, { useEffect, useRef, useState } from 'react';

/**
 * Menu latéral mobile (rail QCM / hamburger / Flash) + panneau coulissant.
 * Comportement aligné sur MobileQCM ; réutilisable sur question, solution, flashcard, etc.
 */
export default function MobileStudySideMenu({
    hidden = false,
    isFlashMode = false,
    isMenuOpen = false,
    onMenuOpenChange,
    onMenuAction,
    onOpenQcmStudy,
    onOpenFlashcardStudy,
}) {
    const menuListRef = useRef(null);
    const [showMenuScrollHintTop, setShowMenuScrollHintTop] = useState(false);
    const [showMenuScrollHintBottom, setShowMenuScrollHintBottom] = useState(false);

    useEffect(() => {
        if (hidden || !isMenuOpen) return;
        const el = menuListRef.current;
        if (!el) return;
        const updateHints = () => {
            const hasTop = el.scrollTop > 0;
            const hasBottom = el.scrollTop + el.clientHeight < el.scrollHeight;
            setShowMenuScrollHintTop(hasTop);
            setShowMenuScrollHintBottom(hasBottom);
        };
        updateHints();
        el.addEventListener('scroll', updateHints);
        return () => el.removeEventListener('scroll', updateHints);
    }, [hidden, isMenuOpen]);

    if (hidden) return null;

    const setOpen = (open) => {
        if (typeof onMenuOpenChange === 'function') {
            onMenuOpenChange(open);
        }
    };

    const toggleMenu = () => setOpen(!isMenuOpen);

    return (
        <>
            {isMenuOpen && (
                <div
                    role="presentation"
                    className="fixed inset-0 z-[10068] bg-black/30"
                    onClick={() => setOpen(false)}
                />
            )}
            <div className="fixed right-1 top-[48%] z-[10065] flex -translate-y-1/2 flex-col items-end gap-2">
                <div className="flex flex-col items-end gap-2">
                    <button
                        type="button"
                        onClick={onOpenQcmStudy}
                        title="Practice with QCM (multiple choice)"
                        aria-label="QCM study mode"
                        className={`flex min-w-[4.75rem] flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2 transition-all duration-200 ${
                            !isFlashMode
                                ? 'border-teal-500/55 bg-teal-950/45 text-teal-100 shadow-lg shadow-teal-900/30'
                                : 'border-gray-600/50 bg-gray-800/70 text-gray-400 hover:bg-gray-700/70'
                        }`}
                    >
                        <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                            />
                        </svg>
                        <span className="text-[10px] font-bold leading-tight tracking-wide">QCM</span>
                    </button>
                    <div
                        className="bg-gray-900/60 border border-gray-700 rounded-full p-3 backdrop-blur shadow-lg cursor-pointer select-none"
                        onClick={toggleMenu}
                        title="Menu"
                        aria-label="Menu"
                        aria-expanded={isMenuOpen}
                    >
                        <svg className="w-6 h-6 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </div>
                    <button
                        type="button"
                        onClick={onOpenFlashcardStudy}
                        title="Flash card review for this filter"
                        aria-label="Flash card study mode"
                        className={`flex min-w-[4.75rem] flex-col items-center justify-center gap-0.5 rounded-xl border px-2 py-2 transition-all duration-200 ${
                            isFlashMode
                                ? 'border-amber-500/55 bg-amber-950/45 text-amber-100 shadow-lg shadow-amber-900/30'
                                : 'border-gray-600/50 bg-gray-800/70 text-gray-400 hover:bg-gray-700/70'
                        }`}
                    >
                        <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                        </svg>
                        <span className="text-[9px] font-bold leading-[1.1] text-center">
                            Flash
                            <br />
                            card
                        </span>
                    </button>
                </div>
            </div>

            <div
                className={`fixed right-0 top-1/2 -translate-y-1/2 transition-transform duration-200 ${
                    isMenuOpen ? 'translate-x-0' : 'translate-x-full'
                } pointer-events-none`}
                style={{ zIndex: 10070 }}
            >
                <div className="pointer-events-auto w-20 bg-gray-900/80 border-l border-gray-700 rounded-l-xl shadow-2xl backdrop-blur relative">
                    <div ref={menuListRef} className="max-h-56 overflow-y-auto py-3">
                        <button
                            onClick={(e) => onMenuAction('qcm', e)}
                            className="w-full flex flex-col items-center gap-1 py-3 hover:bg-gray-800/60 text-gray-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                                />
                            </svg>
                            <span className="text-[10px]">QCM</span>
                        </button>
                        <button
                            onClick={(e) => onMenuAction('what_is', e)}
                            className="w-full flex flex-col items-center gap-1 py-3 hover:bg-gray-800/60 text-gray-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 10h.01M12 10h.01M16 10h.01M9 16h6m2 5H7a2 2 0 01-2-2V7a2 2 0 012-2h5l2 2h5a2 2 0 012 2v10a2 2 0 01-2 2z"
                                />
                            </svg>
                            <span className="text-[10px]">What is?</span>
                        </button>
                        <button
                            onClick={(e) => onMenuAction('what_is_list', e)}
                            className="w-full flex flex-col items-center gap-1 py-3 hover:bg-gray-800/60 text-gray-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            <span className="text-[10px]">What is list</span>
                        </button>
                        <button
                            onClick={(e) => onMenuAction('dashboard', e)}
                            className="w-full flex flex-col items-center gap-1 py-3 hover:bg-gray-800/60 text-gray-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 12l2-2m0 0l7-7 7 7M13 5v6h6M5 10v10a1 1 0 001 1h3m10-11v11a1 1 0 01-1 1h-3"
                                />
                            </svg>
                            <span className="text-[10px]">Dashboard</span>
                            <span className="text-[9px] text-gray-400 font-medium">soon</span>
                        </button>
                        <button
                            onClick={(e) => onMenuAction('session', e)}
                            className="w-full flex flex-col items-center gap-1 py-3 hover:bg-gray-800/60 text-gray-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 20h5V4H2v16h5m10 0v-6H7v6m10 0H7m10-10H7m10-4H7"
                                />
                            </svg>
                            <span className="text-[10px]">Session</span>
                        </button>
                        <button
                            onClick={(e) => onMenuAction('home', e)}
                            className="w-full flex flex-col items-center gap-1 py-3 hover:bg-gray-800/60 text-gray-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 12l2-2m0 0l7-7 7 7M13 5v14m8-7h-3m-4 0H7m-4 0H3"
                                />
                            </svg>
                            <span className="text-[10px]">Home</span>
                        </button>
                        <button
                            onClick={(e) => onMenuAction('disconnect', e)}
                            className="w-full flex flex-col items-center gap-1 py-3 hover:bg-gray-800/60 text-gray-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1"
                                />
                            </svg>
                            <span className="text-[10px]">Disconnect</span>
                        </button>
                    </div>
                    {showMenuScrollHintTop && (
                        <div className="absolute -top-1 left-0 right-0 flex justify-center">
                            <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M10 3a1 1 0 01.832.445l4 6a1 1 0 11-1.664 1.11L10 5.882 6.832 10.555a1 1 0 01-1.664-1.11l4-6A1 1 0 0110 3z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                    )}
                    {showMenuScrollHintBottom && (
                        <div className="absolute -bottom-1 left-0 right-0 flex justify-center">
                            <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M10 17a1 1 0 01-.832-.445l-4-6a1 1 0 111.664-1.11L10 14.118l3.168-4.673a1 1 0 111.664 1.11l-4 6A1 1 0 0110 17z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
